import { useMutation } from '@apollo/client';
import { Box, Button, ButtonAppearance, ButtonSize, Heading, Icon, IconSize, ModalBody } from '@bechtel/gds';
import { UiMessageKey, UiTextEnum } from '@bechtel/sr-types';
import { Formik } from 'formik';
import { get } from 'lodash';
import { FC, lazy } from 'react';
import { useParams } from 'react-router';
import { object, string } from 'yup';
import { MultiLingualText, useAppUser, useLanguage } from '~/components';
import { ADD_REVISION_NOTE } from '~/gql';
import {
  ModalFooterStyled,
  ModalHeaderStyled,
  ModalStyled,
  TextAreaStyled,
} from '../change-management-toolbar/change-management-notes/styled';
import { FooterButtonStyled, ModalHeaderContainerStyled } from './styled';
import { AddRevisionNotesModalProps, RevNoteLevel, initialValues } from './types';
const MessageModal = lazy(() => import('~/components/organisms/message-modal/message'));

export const AddRevisionNoteModal: FC<AddRevisionNotesModalProps> = ({ commodityName, modalState, setModalState }) => {
  const { getMultiLingualTextByKey } = useLanguage();
  const params = useParams();
  const { userAccount } = useAppUser();
  const tenantConfig = get(userAccount, 'tenantConfig');
  const isOptional = tenantConfig?.revNoteDefinitionLevel == RevNoteLevel.OPT;

  const validationSchema = object().shape({
    note: string()
      .trim()
      .min(1, `Please enter atleast one character for note`)
      .max(255, `Please limit name to 255 characters`)
      .required(`Note is mandatory`),
  });

  const [addRevisionNote, { data: addRevisionData }] = useMutation(ADD_REVISION_NOTE);

  const addNote = async (values) => {
    return await addRevisionNote({
      variables: {
        addRevisionNoteInput: {
          parentId: params.id,
          noteCommodityType: commodityName,
          note: values,
          cardRevisionQuestion: 'NOT_ASKED',
        },
      },
    });
  };

  const submitForm = async (values) => {
    await addNote(values);
    setModalState(false);
  };

  const errorKeys: UiMessageKey[] =
    get(addRevisionData, 'addRevisionNote.infos') ?? get(addRevisionData, 'addRevisionNote.errors') ?? [];

  const onClose = () => {
    setModalState(false);
  };

  return (
    <>
      <ModalStyled visible={modalState}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={(values) => {
            submitForm(values ? values.note : '');
          }}
        >
          {({ values, handleChange, handleBlur, handleSubmit, dirty, isValid, touched }) => {
            return (
              <>
                <ModalHeaderStyled title="">
                  <ModalHeaderContainerStyled>
                    <Box>
                      <Heading size="500" weight="500">
                        <MultiLingualText>Revision Note</MultiLingualText>
                      </Heading>
                    </Box>
                    {isOptional && (
                      <Box onClick={onClose}>
                        <Button
                          size={ButtonSize.SMALL}
                          style={{ background: 'transparent', boxShadow: 'none' }}
                          onClick={() => close()}
                        >
                          <Icon icon="times" iconStyle="regular" size={IconSize.LARGE} />
                        </Button>
                      </Box>
                    )}
                  </ModalHeaderContainerStyled>
                </ModalHeaderStyled>
                {/* @ts-ignore adjust in GDS component to take child as prop  */}
                <ModalBody
                  css={`
                    flex-direction: column;
                  `}
                >
                  <form style={{ width: '100%' }}>
                    <Box flexDirection={{ sm: 'column' }} p={{ sm: '500' }}>
                      <Box pb={{ sm: '400' }}>
                        <MultiLingualText size="300" color="primary.700">
                          Enter a description of the change
                        </MultiLingualText>
                      </Box>
                      <TextAreaStyled
                        name="note"
                        onChange={handleChange}
                        value={values.note}
                        placeholder={getMultiLingualTextByKey(UiTextEnum.ENTER_COMMENTS)}
                        rows={7}
                        onBlur={handleBlur}
                        changed={dirty}
                      />
                    </Box>
                  </form>
                </ModalBody>
                <ModalFooterStyled>
                  <Box p={{ sm: '500' }} pt={{ sm: '200' }}>
                    {isOptional && (
                      <FooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={onClose}>
                        <Box px={{ sm: '500' }}>
                          <MultiLingualText>Cancel</MultiLingualText>
                        </Box>
                      </FooterButtonStyled>
                    )}
                    <FooterButtonStyled onClick={() => handleSubmit()} disabled={!dirty || !isValid || !touched}>
                      <Box px={{ sm: '500' }}>
                        <MultiLingualText>Save</MultiLingualText>
                      </Box>
                    </FooterButtonStyled>
                  </Box>
                </ModalFooterStyled>
              </>
            );
          }}
        </Formik>
      </ModalStyled>
      {errorKeys && errorKeys.length > 0 && <MessageModal messageKeys={errorKeys}></MessageModal>}
    </>
  );
};
